import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import SliderInput, { formatINR } from '../../components/SliderInput';

const PPF_RATE = 7.1; // current PPF rate in India (FY 2024-25)
const PPF_MIN_YEARS = 15;
const PPF_MAX_DEPOSIT = 150000;

const PPFCalculator = () => {
  const [yearly, setYearly] = useState(150000);
  const [years, setYears] = useState(15);

  const { schedule, maturity, totalDeposit, totalInterest } = useMemo(() => {
    let balance = 0;
    const rows = [];
    for (let y = 1; y <= years; y++) {
      balance += yearly;
      const interest = balance * (PPF_RATE / 100);
      balance += interest;
      rows.push({ year: y, deposit: yearly, interest, balance });
    }
    return {
      schedule: rows,
      maturity: balance,
      totalDeposit: yearly * years,
      totalInterest: balance - yearly * years,
    };
  }, [yearly, years]);

  return (
    <CalculatorLayout
      title="PPF Calculator"
      subtitle="Public Provident Fund maturity calculator at current 7.1% interest rate."
      category="Financial"
      description={<>
        <p>The Public Provident Fund (PPF) is a popular long-term savings scheme by the Government of India. The current interest rate is <strong>{PPF_RATE}% p.a.</strong>, compounded annually. Maximum yearly deposit is ₹1,50,000 and minimum tenure is 15 years.</p>
        <p>Tax benefits: deposits qualify for Section 80C deduction, and both interest and maturity are tax-free (EEE category).</p>
      </>}
      formulaNote={`Annual interest = Balance × ${PPF_RATE}% (compounded yearly)`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <SliderInput label="Yearly Investment" unit="₹" unitColor="saffron" value={yearly} onChange={setYearly} min={500} max={PPF_MAX_DEPOSIT} step={500} format={formatINR} hint={`Max ₹${PPF_MAX_DEPOSIT.toLocaleString('en-IN')} per year (Income Tax rules)`} />
          <SliderInput label="Time Period" unit="yr" value={years} onChange={setYears} min={PPF_MIN_YEARS} max={50} step={1} hint="Min 15 years; can extend in blocks of 5 years" />
        </div>
        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Maturity Amount (Tax-Free)</div>
          <div className="font-display text-3xl md:text-4xl font-bold text-bharat-navy display-font mt-1">{formatINR(maturity)}</div>
          <div className="grid grid-cols-2 gap-3 mt-5">
            <div className="bg-white rounded-md p-3 border border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Deposit</div>
              <div className="display-font font-semibold mt-0.5 text-bharat-navy">{formatINR(totalDeposit)}</div>
            </div>
            <div className="bg-white rounded-md p-3 border border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Interest</div>
              <div className="display-font font-semibold mt-0.5 text-indgreen-700">{formatINR(totalInterest)}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6 bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 font-display font-semibold text-bharat-navy text-sm">Year-wise Growth</div>
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-slate-600">
                <th className="px-3 py-2 text-left font-semibold">Year</th>
                <th className="px-3 py-2 text-right font-semibold">Deposit</th>
                <th className="px-3 py-2 text-right font-semibold">Interest</th>
                <th className="px-3 py-2 text-right font-semibold">Balance</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map(s => (
                <tr key={s.year} className="border-t border-slate-100 display-font">
                  <td className="px-3 py-1.5 text-slate-500">{s.year}</td>
                  <td className="px-3 py-1.5 text-right text-bharat-navy">{formatINR(s.deposit)}</td>
                  <td className="px-3 py-1.5 text-right text-indgreen-700">{formatINR(s.interest)}</td>
                  <td className="px-3 py-1.5 text-right text-saffron-700 font-semibold">{formatINR(s.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </CalculatorLayout>
  );
};

export default PPFCalculator;
